import { CareerJourneyBoardQuery } from 'core/graphql';
import {
  ISkillProgress,
  transformSkillProgress,
} from 'core/hooks/Growth/transform/transformSkillProgress';

/** Коды этапов — замкнутое объединение, а не свободная строка. */
export type CareerStageCode =
  | 'goal'
  | 'learning'
  | 'portfolio'
  | 'cv'
  | 'applications'
  | 'interview';

/** Четыре этапа маршрута на главной (ТЗ 0071) — проекция шести этапов расчёта. */
export type CareerSectionCode = 'direction' | 'preparation' | 'search' | 'recruitment';

export type CareerSectionState = 'not_started' | 'in_progress' | 'done' | 'blocked';

/** Что закрыто не-участнику. Ключи приходят с сервера, здесь только подписи. */
export type CareerCapability = 'lessons' | 'vacancies' | 'applications';

/**
 * 🔴 Объединения объявлены типами намеренно. Добавление седьмого этапа или
 * шестого статуса ломает КОМПИЛЯЦИЮ в месте использования — и человек
 * вспоминает про `EXPLICIT_KEYS` в locale-тесте ДО релиза, а не после.
 * Свободная строка молча уехала бы студенту сырым ключом на экран.
 */
export interface ICareerStage {
  key: CareerStageCode;
  done: number;
  total: number;
  isDone: boolean;
  isBlocked: boolean;
  blockReason: string;
  route: string;
  targetId: string;
}

export interface ICareerGoal {
  trackCode: string;
  title: string;
  icon: string;
  isChosen: boolean;
  skillsMine: number;
  skillsTotal: number;
  /** ТЗ 0071: `null`, пока цель не выбрана — персонального «10 из 22» нет. */
  skillProgress: ISkillProgress | null;
  salaryFrom: number | null;
  salaryTo: number | null;
  salaryCurrency: string;
}

/**
 * Одно действие плана (ТЗ 0071, п. 9.2).
 *
 * 🔴 Приоритет выбирает СЕРВЕР. Клиент по `kind` и `reasonCode` подбирает
 * подписи на языке оболочки, а данные — название урока, «6 из 7», минуты,
 * результат, компанию — берёт отсюда как есть.
 */
export interface ICareerAction {
  id: string;
  kind: string;
  stageKey: CareerStageCode | null;
  /** Готовый адрес. Клиент его не склеивает. */
  route: string;
  targetId: string;
  reasonCode: string;
  subjectTitle: string;
  position: number | null;
  positionTotal: number | null;
  expectedOutcome: string;
  estimatedMinutes: number | null;
  dueDate: string;
}

export interface ICareerSectionMetric {
  key: string;
  value: number;
  target: number | null;
  route: string;
  isDone: boolean;
  isBlocked: boolean;
}

export interface ICareerSection {
  key: CareerSectionCode;
  state: CareerSectionState;
  /** Этап следующего действия — раскрыт на главной. */
  isCurrent: boolean;
  route: string;
  isBlocked: boolean;
  blockReason: string;
  metrics: ICareerSectionMetric[];
}

export interface ICareerCvCheck {
  /** `has_name`, `has_skills`, … — ключ подписи в локали. */
  key: string;
  isDone: boolean;
}

export interface ICareerLocked {
  key: CareerCapability;
  reasonCode: string;
}

export interface IJourneyApplication {
  id: string;
  companyName: string;
  position: string;
  status: string;
  appliedAt: string;
  nextActionAt: string;
}

export interface ICareerJourney {
  isMember: boolean;
  isPreview: boolean;
  memberStatus: string;
  goal: ICareerGoal | null;
  stages: ICareerStage[];
  /** 'orientation' | 'preparation' | 'search' | 'recruitment' | 'placed'. */
  phase: string;
  /** Цель выбрана ЯВНО. Подставленная по умолчанию профессия целью не является. */
  hasGoal: boolean;
  primaryAction: ICareerAction | null;
  /** До двух следующих шагов — «Najbliższe kroki». */
  secondaryActions: ICareerAction[];
  sections: ICareerSection[];
  locked: ICareerLocked[];
  cvChecklist: ICareerCvCheck[];
  applications: {
    total: number;
    active: number;
    applied: number;
    interview: number;
    offer: number;
    rejected: number;
    hired: number;
    nextFollowUp: IJourneyApplication | null;
    recent: IJourneyApplication[];
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const action = (row: any): ICareerAction => ({
  id: row.id,
  kind: row.kind,
  stageKey: (row.stageKey as CareerStageCode) ?? null,
  route: row.route,
  targetId: row.targetId ?? '',
  reasonCode: row.reasonCode ?? '',
  subjectTitle: row.subjectTitle ?? '',
  position: row.position ?? null,
  positionTotal: row.positionTotal ?? null,
  expectedOutcome: row.expectedOutcome ?? '',
  estimatedMinutes: row.estimatedMinutes ?? null,
  dueDate: row.dueDate ?? '',
});

const application = (row: any): IJourneyApplication => ({
  id: row?.id ?? '',
  companyName: row?.companyName ?? '',
  position: row?.position ?? '',
  status: row?.status ?? '',
  appliedAt: row?.appliedAt ?? '',
  nextActionAt: row?.nextActionAt ?? '',
});

/**
 * Ответ сервера → форма, удобная экрану.
 *
 * 🔴 Здесь НЕТ арифметики. ТЗ 0071 сняло и последнюю — сумму этапов
 * «13 / 20»: она складывала несопоставимые числа (уроки, галочки резюме,
 * отклики) и повторяла этапы маршрута. Счётчики считает сервер теми же
 * функциями, что обслуживают экраны, куда ведут кнопки.
 */
export const transformCareerJourney = (data?: CareerJourneyBoardQuery): ICareerJourney | null => {
  const raw = data?.myCareerJourney;
  if (!raw) {
    return null;
  }

  const stages: ICareerStage[] = (raw.stages ?? []).map((stage) => ({
    key: stage.key as CareerStageCode,
    done: stage.done,
    total: stage.total,
    isDone: stage.isDone,
    isBlocked: stage.isBlocked,
    blockReason: stage.blockReason ?? '',
    route: stage.route,
    targetId: stage.targetId ?? '',
  }));

  return {
    isMember: raw.isMember,
    isPreview: raw.isPreview,
    memberStatus: raw.memberStatus ?? '',
    goal: raw.goal
      ? {
          trackCode: raw.goal.trackCode,
          title: raw.goal.title,
          icon: raw.goal.icon,
          isChosen: raw.goal.isChosen,
          skillsMine: raw.goal.targetSkillsMine,
          skillsTotal: raw.goal.targetSkillsTotal,
          skillProgress: raw.goal.skillProgress
            ? transformSkillProgress(raw.goal.skillProgress)
            : null,
          salaryFrom: raw.goal.salaryFrom ?? null,
          salaryTo: raw.goal.salaryTo ?? null,
          salaryCurrency: raw.goal.salaryCurrency,
        }
      : null,
    stages,
    phase: raw.phase,
    hasGoal: raw.hasGoal,
    primaryAction: raw.primaryAction ? action(raw.primaryAction) : null,
    secondaryActions: (raw.secondaryActions ?? []).map(action),
    sections: (raw.sections ?? []).map((section) => ({
      key: section.key as CareerSectionCode,
      state: section.state as CareerSectionState,
      isCurrent: section.isCurrent,
      route: section.route,
      isBlocked: section.isBlocked,
      blockReason: section.blockReason ?? '',
      metrics: section.metrics.map((metric) => ({
        key: metric.key,
        value: metric.value,
        target: metric.target ?? null,
        route: metric.route ?? '',
        isDone: Boolean(metric.isDone),
        isBlocked: Boolean(metric.isBlocked),
      })),
    })),
    locked: (raw.locked ?? []).map((row) => ({
      key: row.key as CareerCapability,
      reasonCode: row.reasonCode,
    })),
    cvChecklist: (raw.cvChecklist ?? []).map((row) => ({
      key: row.key,
      isDone: row.isDone,
    })),
    applications: {
      total: raw.applications.total,
      active: raw.applications.active,
      applied: raw.applications.applied,
      interview: raw.applications.interview,
      offer: raw.applications.offer,
      rejected: raw.applications.rejected,
      hired: raw.applications.hired,
      nextFollowUp: raw.applications.nextFollowUp
        ? application(raw.applications.nextFollowUp)
        : null,
      recent: (raw.applications.recent ?? []).map(application),
    },
  };
};
